import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { oneKlicks } from "../../../SDK/oneclick";
import { toast } from "react-toastify";

const STATUS_OPTIONS = [
    { label: "Pending", value: "pending" },
    { label: "Under Review", value: "under_review" },
    { label: "Approved", value: "approved" },
    { label: "Rejected", value: "rejected" },
    { label: "Partially Settled", value: "partially_settled" },
];

const NewClaim = () => {
    const navigate = useNavigate();
    const { userData } = useAuth();

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [policies, setPolicies] = useState([]);

    const [form, setForm] = useState({
        customer_id: "",
        policy_id: "",
        claim_number: "",
        claim_amount: "",
        claim_reason: "",
        claim_date: new Date().toISOString().slice(0, 10),
        incident_date: "",
        status: "pending",
        documents: [],
        admin_notes: "",
    });

    const companyId = useMemo(() => userData?.company_id || userData?.companyId || userData?.company, [userData]);

    useEffect(() => {
        fetchReferenceData();
    }, [companyId]);

    const fetchReferenceData = async () => {
        setLoading(true);
        try {
            const baseConditions = companyId ? { company_id: companyId } : {};

            const customerRes = await oneKlicks.read("", "insurance_customers", baseConditions, "id DESC", "", "", "0,200");
            console.log("Insurance claim customers response:", customerRes);
            let customerRows = [];
            if (customerRes?.success) {
                customerRows = Array.isArray(customerRes?.data)
                    ? customerRes.data
                    : Array.isArray(customerRes?.data?.items)
                        ? customerRes.data.items
                        : [];

                if (customerRows.length === 0 && Object.keys(baseConditions).length > 0) {
                    console.log("No customers found with company filter for claims. Trying unfiltered customers fetch.");
                    const fallbackRes = await oneKlicks.read("", "insurance_customers", {}, "id DESC", "", "", "0,200");
                    console.log("Insurance claim customers fallback response:", fallbackRes);
                    if (fallbackRes?.success) {
                        customerRows = Array.isArray(fallbackRes?.data)
                            ? fallbackRes.data
                            : Array.isArray(fallbackRes?.data?.items)
                                ? fallbackRes.data.items
                                : [];
                    }
                }
            }
            setCustomers(customerRows);

            const policyRes = await oneKlicks.read("", "insurance_policies", baseConditions, "id DESC", "", "", "0,200");
            console.log("Insurance claim policies response:", policyRes);
            let policyRows = [];
            if (policyRes?.success) {
                policyRows = Array.isArray(policyRes?.data)
                    ? policyRes.data
                    : Array.isArray(policyRes?.data?.items)
                        ? policyRes.data.items
                        : [];

                if (policyRows.length === 0 && Object.keys(baseConditions).length > 0) {
                    console.log("No policies found with company filter for claims. Trying unfiltered policies fetch.");
                    const fallbackRes = await oneKlicks.read("", "insurance_policies", {}, "id DESC", "", "", "0,200");
                    console.log("Insurance claim policies fallback response:", fallbackRes);
                    if (fallbackRes?.success) {
                        policyRows = Array.isArray(fallbackRes?.data)
                            ? fallbackRes.data
                            : Array.isArray(fallbackRes?.data?.items)
                                ? fallbackRes.data.items
                                : [];
                    }
                }
            }
            setPolicies(policyRows);
        } catch (error) {
            console.error("Error loading reference data:", error);
            toast.error("Failed to load customers and policies");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleDocumentChange = (e) => {
        const { value } = e.target;
        const documents = value
            .split("\n")
            .map((line) => line.trim())
            .filter(Boolean);
        setForm((prev) => ({ ...prev, documents }));
    };

    const saveClaim = async (payload) => {
        return oneKlicks.create("", "insurance_claims", payload);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.customer_id || !form.policy_id) {
            toast.error("Please select both customer and policy");
            return;
        }

        if (!form.claim_amount) {
            toast.error("Claim amount is required");
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                customer_id: form.customer_id ? Number(form.customer_id) : null,
                policy_id: form.policy_id ? Number(form.policy_id) : null,
                claim_number: form.claim_number || undefined,
                claim_amount: Number(form.claim_amount),
                claim_reason: form.claim_reason,
                claim_date: form.claim_date,
                incident_date: form.incident_date || null,
                status: form.status,
                approved_amount: null,
                admin_notes: form.admin_notes || null,
                documents: form.documents.length > 0 ? JSON.stringify(form.documents) : null,
                company_id: companyId || null,
                agent_id: userData?.agent_id || null,
            };

            const response = await saveClaim(payload);

            if (response?.success) {
                toast.success("Claim filed successfully");
                navigate("/insurance/claims");
            } else {
                toast.error(response?.message || "Failed to file claim");
            }
        } catch (error) {
            console.error("Error saving claim:", error);
            toast.error("Failed to file claim");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="subbody" style={{ width: "100%", padding: "24px" }}>
            <div className="dc-haslayout dc-dbsectionspace" style={{ maxWidth: "1080px", margin: "0 auto" }}>
                <div className="dc-dashboardbox">
                    <div
                        className="dc-dashboardboxtitle"
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            flexWrap: "wrap",
                            gap: "12px",
                        }}
                    >
                        <div>
                            <h2>File New Claim</h2>
                            <p style={{ color: "#6c757d", marginTop: "4px" }}>
                                Submit a new insurance claim using the details below.
                            </p>
                        </div>
                        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => navigate("/insurance/claims")}
                                disabled={submitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                form="insurance-claim-form"
                                className="btn btn-primary"
                                disabled={submitting}
                            >
                                {submitting ? "Submitting..." : "Submit Claim"}
                            </button>
                        </div>
                    </div>

                    <div className="dc-dashboardboxcontent" style={{ padding: "24px" }}>
                        {loading ? (
                            <div className="text-center p-5">
                                <div className="spinner-border" role="status">
                                    <span className="sr-only">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <form id="insurance-claim-form" className="dc-formtheme" onSubmit={handleSubmit}>
                                <div className="row" style={{ rowGap: "24px" }}>
                                    <div className="col-12 col-md-6">
                                        <div className="form-group">
                                            <label htmlFor="customer_id">Customer</label>
                                            <select
                                                id="customer_id"
                                                name="customer_id"
                                                className="form-control"
                                                value={form.customer_id}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">Select Customer</option>
                                                {customers.map((customer) => {
                                                    const primary = customer.name || customer.full_name || customer.customer_name || `Customer #${customer.id}`;
                                                    const contact = customer.email || customer.phone || "No contact";
                                                    return (
                                                        <option key={customer.id} value={customer.id}>
                                                            {primary} – {contact}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6">
                                        <div className="form-group">
                                            <label htmlFor="policy_id">Policy</label>
                                            <select
                                                id="policy_id"
                                                name="policy_id"
                                                className="form-control"
                                                value={form.policy_id}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">Select Policy</option>
                                                {policies.map((policy) => {
                                                    const primary = policy.policy_number || policy.policy_code || `Policy #${policy.id}`;
                                                    const secondary = policy.policy_holder_name || policy.coverage_type || "";
                                                    return (
                                                        <option key={policy.id} value={policy.id}>
                                                            {primary}
                                                            {secondary ? ` – ${secondary}` : ""}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6 col-xl-4">
                                        <div className="form-group">
                                            <label htmlFor="claim_number">Claim Number (optional)</label>
                                            <input
                                                type="text"
                                                id="claim_number"
                                                name="claim_number"
                                                className="form-control"
                                                placeholder="Auto-generated if left blank"
                                                value={form.claim_number}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6 col-xl-4">
                                        <div className="form-group">
                                            <label htmlFor="claim_amount">Claim Amount (₹)</label>
                                            <input
                                                type="number"
                                                id="claim_amount"
                                                name="claim_amount"
                                                className="form-control"
                                                min="0"
                                                step="0.01"
                                                value={form.claim_amount}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6 col-xl-4">
                                        <div className="form-group">
                                            <label htmlFor="status">Status</label>
                                            <select
                                                id="status"
                                                name="status"
                                                className="form-control"
                                                value={form.status}
                                                onChange={handleChange}
                                                required
                                            >
                                                {STATUS_OPTIONS.map((status) => (
                                                    <option key={status.value} value={status.value}>
                                                        {status.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <div className="form-group">
                                            <label htmlFor="claim_reason">Claim Reason / Description</label>
                                            <textarea
                                                id="claim_reason"
                                                name="claim_reason"
                                                className="form-control"
                                                rows="4"
                                                placeholder="Provide details about the incident..."
                                                value={form.claim_reason}
                                                onChange={handleChange}
                                                required
                                            ></textarea>
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6">
                                        <div className="form-group">
                                            <label htmlFor="claim_date">Claim Date</label>
                                            <input
                                                type="date"
                                                id="claim_date"
                                                name="claim_date"
                                                className="form-control"
                                                value={form.claim_date}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6">
                                        <div className="form-group">
                                            <label htmlFor="incident_date">Incident Date (optional)</label>
                                            <input
                                                type="date"
                                                id="incident_date"
                                                name="incident_date"
                                                className="form-control"
                                                value={form.incident_date}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6">
                                        <div className="form-group">
                                            <label htmlFor="documents">Document URLs (one per line)</label>
                                            <textarea
                                                id="documents"
                                                className="form-control"
                                                rows="3"
                                                placeholder="https://..."
                                                value={form.documents.join("\n")}
                                                onChange={handleDocumentChange}
                                            ></textarea>
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <div className="form-group">
                                            <label htmlFor="admin_notes">Internal Notes (optional)</label>
                                            <textarea
                                                id="admin_notes"
                                                name="admin_notes"
                                                className="form-control"
                                                rows="3"
                                                placeholder="Any internal notes or context"
                                                value={form.admin_notes}
                                                onChange={handleChange}
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewClaim;
